import { MatterStatuteEstimate } from "./MatterStatuteEstimate";
import {
  ActionConfirmationModal,
  Button,
  CheckboxFormField,
  DateFormField,
  Form,
  FormField,
  FormItem,
  FormSubmitButton,
  TextAreaFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useSyncStatuteDefendantConnection } from "@/lib/data/react-query/connections/statute-contact-connection";
import {
  useCreateMatterStatuteOfLimitations,
  useDeleteMatterStatuteOfLimitations,
  useUpdateMatterStatuteOfLimitations,
} from "@/lib/data/react-query/matters/matter-statute-of-limitations";
import { ContactConnectionInFormSchema } from "@/lib/data/schemas/contact/ContactSchema";
import { MATTER_CONTACT_CONNECTION_DEFAULT_VALUES } from "@/lib/data/static/formStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { APIError, Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { getConnectionFormContact } from "@/lib/utils/connections/item-contact-connections";
import {
  getMatterPartyConnectionRole,
  getMatterPartyConnections,
} from "@/lib/utils/connections/matter-party-connections";
import {
  createStatuteDefendantConnectionChildInput,
  getStatuteDefendantConnection,
} from "@/lib/utils/connections/statute-contact-connections";
import { parseDate } from "@internationalized/date";
import { zodResolver } from "@hookform/resolvers/zod";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AddOrEditMatterStatuteOfLimitationsSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matter: Matter;
  statuteOfLimitationsId?: string;
  defaultDefendantContactId?: string;
}

const formSchema = z.object({
  date: z.string().min(1, "Statute of limitations date is required"),
  defendantContactId: ContactConnectionInFormSchema.refine(
    (contact) => Boolean(contact?.id),
    {
      message: "Defendant is required",
    },
  ).default(MATTER_CONTACT_CONNECTION_DEFAULT_VALUES),
  confirmed: z.boolean().optional(),
  description: z
    .string()
    .trim()
    .max(5000, "Description must be 5,000 characters or less")
    .optional(),
});

const defaultValues: z.infer<typeof formSchema> = {
  date: "",
  defendantContactId: MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  confirmed: false,
  description: "",
};

export const AddOrEditMatterStatuteOfLimitationsSlideover = ({
  open,
  setOpen,
  matter,
  statuteOfLimitationsId,
  defaultDefendantContactId,
}: AddOrEditMatterStatuteOfLimitationsSlideoverProps) => {
  const { hasActionPermission } = useUser();
  const createStatute = useCreateMatterStatuteOfLimitations({
    showSuccessToast: false,
  });
  const updateStatute = useUpdateMatterStatuteOfLimitations({
    showSuccessToast: false,
  });
  const deleteStatute = useDeleteMatterStatuteOfLimitations({
    showSuccessToast: false,
  });
  const syncStatuteDefendant = useSyncStatuteDefendantConnection();
  const [isDeleting, setIsDeleting] = useState(false);

  const activeStatute = useMemo(
    () =>
      matter.statutesOfLimitations.find(
        (statute) => statute.id === statuteOfLimitationsId,
      ),
    [matter, statuteOfLimitationsId],
  );
  const defendantConnections = useMemo(
    () =>
      getMatterPartyConnections(matter).filter(
        (connection) =>
          getMatterPartyConnectionRole(connection) === "Defendant",
      ),
    [matter],
  );
  const activeStatuteDefendantConnection =
    getStatuteDefendantConnection(activeStatute);
  const availableDefendantConnections = useMemo(() => {
    const defendantMap = new Map<
      string,
      (typeof defendantConnections)[number]
    >();

    [
      ...defendantConnections,
      ...(activeStatuteDefendantConnection
        ? [activeStatuteDefendantConnection]
        : []),
    ].forEach((connection) => {
      defendantMap.set(connection.targetObjectId, connection);
    });

    return [...defendantMap.values()];
  }, [activeStatuteDefendantConnection, defendantConnections]);
  const defaultDefendantConnection = useMemo(
    () =>
      defaultDefendantContactId
        ? availableDefendantConnections.find(
            (connection) =>
              connection.targetObjectId === defaultDefendantContactId,
          )
        : availableDefendantConnections[0],
    [availableDefendantConnections, defaultDefendantContactId],
  );
  const editing = Boolean(activeStatute);
  const hasViewPermission = Boolean(true);
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatuteOfLimitations,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatuteOfLimitations,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatuteOfLimitations,
    ACTION_PERMISSION_VALUES.delete,
  );
  const hasConfirmStatutePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatuteOfLimitations,
    ACTION_PERMISSION_VALUES.update,
  );
  const isReadOnly =
    !hasViewPermission ||
    (editing ? !hasUpdatePermission : !hasCreatePermission);
  const canSubmit = editing ? hasUpdatePermission : hasCreatePermission;
  const formValues = useMemo(() => {
    const defendantContactForForm = activeStatuteDefendantConnection
      ? getConnectionFormContact(activeStatuteDefendantConnection)
      : defaultDefendantConnection
        ? getConnectionFormContact(defaultDefendantConnection)
        : MATTER_CONTACT_CONNECTION_DEFAULT_VALUES;

    return editing && activeStatute
      ? {
          date: activeStatute.date
            ? dayjs(activeStatute.date).format("YYYY-MM-DD")
            : "",
          defendantContactId: defendantContactForForm,
          confirmed: activeStatute.confirmed ?? false,
          description: activeStatute.description ?? "",
        }
      : {
          ...defaultValues,
          defendantContactId: defendantContactForForm,
        };
  }, [
    activeStatute,
    activeStatuteDefendantConnection,
    defaultDefendantConnection,
    editing,
  ]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const selectedDate = form.watch("date");

  const isDeletePending = deleteStatute.isPending;
  const isLoading =
    createStatute.isPending ||
    updateStatute.isPending ||
    isDeletePending ||
    syncStatuteDefendant.isPending;
  const statuteModalFormId = "add-or-edit-statute-modal-form";

  useEffect(() => {
    form.reset(open ? formValues : defaultValues);
  }, [form, formValues, open]);

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!canSubmit) return;

    const payload = {
      matterId: matter.id,
      date: values.date,
      confirmed: hasConfirmStatutePermission
        ? (values.confirmed ?? false)
        : activeStatute
          ? (activeStatute.confirmed ?? false)
          : false,
      description: values.description?.trim() || undefined,
    };

    if (editing && activeStatute) {
      const updatedStatute = await updateStatute.mutateAsync({
        ...payload,
        id: activeStatute.id,
        dateModified: activeStatute.dateModified,
      });
      await syncStatuteDefendant.mutateAsync({
        currentStatute: activeStatute,
        desiredContactId: values.defendantContactId.id,
        matterId: matter.id,
        statuteId: updatedStatute.data.id,
      });
      toast.success("Statute of limitations updated.");
    } else {
      await createStatute.mutateAsync({
        ...payload,
        connections: [
          createStatuteDefendantConnectionChildInput(
            values.defendantContactId.id,
          ),
        ],
      });
      toast.success("Statute of limitations created.");
    }

    setOpen(false);
  };

  const handleDelete = async () => {
    if (!activeStatute || !hasDeletePermission) return;

    try {
      await deleteStatute.mutateAsync({
        matterId: matter.id,
        id: activeStatute.id,
      });

      toast.success("Statute of limitations deleted.");
      setIsDeleting(false);
      setOpen(false);
    } catch (error) {
      toastAPIErrorMessage(
        error as AxiosError<APIError>,
        "Could not delete statute of limitations",
      );
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={
            editing
              ? "Update statute of limitations"
              : "Create statute of limitations"
          }
          description={
            editing
              ? "Update the statute of limitations below"
              : "Create a new statute of limitations"
          }
          size="3xl"
          loading={isLoading}
          isDirty={form.formState.isDirty}
          bodyFormId={statuteModalFormId}
          bodyFormOnSubmit={form.handleSubmit(handleSubmit)}
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                {editing && hasDeletePermission && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      setIsDeleting(true);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                <FormSubmitButton
                  submitFormId={statuteModalFormId}
                  loading={isLoading}
                  disabled={!canSubmit || isLoading}
                  overrideDisabled
                />
              </div>
            </div>
          }
        >
          <fieldset className="grid grid-cols-2 gap-4" disabled={isReadOnly}>
            <FormField
              control={form.control}
              name="defendantContactId"
              render={({ fieldState }) => (
                <FormItem className="col-span-2">
                  {fieldState.error ? (
                    <p
                      className="text-destructive text-xs font-medium"
                      data-testid="error-message-defendant"
                    >
                      {fieldState.error.message}
                    </p>
                  ) : null}
                </FormItem>
              )}
            />
            {!isReadOnly && (
              <MatterStatuteEstimate
                key={`${matter.id}:${matter.dateModified}:${form.watch("defendantContactId").id}:${open}`}
                matter={matter}
                defendantId={form.watch("defendantContactId").id}
                onApply={(date) => {
                  form.setValue("date", date, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              />
            )}
            <DateFormField
              register={form.register("date")}
              name="date"
              value={selectedDate ? parseDate(selectedDate) : null}
              label="Statute of limitations date*"
            />
            <CheckboxFormField
              className="mt-0 self-end"
              register={form.register("confirmed")}
              label="Statute of limitations confirmed?"
              disabled={isReadOnly || !hasConfirmStatutePermission}
              disabledMessage={
                !isReadOnly && !hasConfirmStatutePermission
                  ? "You do not have permission to confirm statutes of limitations."
                  : undefined
              }
            />
            <div className="col-span-2">
              <TextAreaFormField
                register={form.register("description")}
                label="Description"
                textAreaClassName="field-sizing-content max-h-[20lh]"
              />
            </div>
          </fieldset>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this statute of limitations?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
        isActionPending={isDeletePending}
      />
    </>
  );
};
